import React, { useMemo } from "react";
import { groupBy, sortBy, uniqBy } from "@common/helpers/collections";
import { userWorkspaceRolesInOrder } from "@common/userWorkspaceRoles";
import { WorkspaceMember } from "@common/types/workspace";
import { Organization } from "@common/types/organization";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import {
    WorkspaceMembersColumn,
    WorkspaceMembersView,
} from "@common/types/views/workspaceMembersView";
import { ViewDensity, ViewType } from "@common/types/views/views";
import {
    useCurrentWorkspaceMembers,
    usePendingWorkspaceInvites,
} from "@api/workspaceMember.api";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { WorkspaceMembersRow } from "./workspaceMembersRow";
import { workspaceMembersViewConfig } from "./workspaceMembersViewConfig";

const DEFAULT_VIEW: WorkspaceMembersView = {
    type: ViewType.WORKSPACE_MEMBERS,
    columns: [
        { id: WorkspaceMembersColumn.USER },
        { id: WorkspaceMembersColumn.ENTITIES },
        { id: WorkspaceMembersColumn.ROLE },
        { id: WorkspaceMembersColumn.LAST_ACTIVE },
        { id: WorkspaceMembersColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: { density: ViewDensity.MD },
};

interface Props {
    search?: string;
}

export const WorkspaceMemberList: React.FC<Props> = ({ search }) => {
    const { data: members, isFetched: membersFetched } =
        useCurrentWorkspaceMembers();

    const { data: pendingInvites, isFetched: pendingInvitesFetched } =
        usePendingWorkspaceInvites();

    const rows = useMemo((): WorkspaceMembersRow[] => {
        if (!members) {
            return [];
        }

        const unaffiliated = members.filter((m) => !m.organizationId);

        const orgs = uniqBy(
            members
                .map((m) => m.organization)
                .filter((o): o is Organization => !!o),
            (o) => o.id,
        );

        const byOrg = groupBy(members, (m) => m.organizationId);

        const orgRows: WorkspaceMembersRow[] = orgs.map((organization) => ({
            id: `org-${organization.id}`,
            kind: "organization" as const,
            organization,
            organizationMembers: byOrg[organization.id] ?? [],
        }));

        let membersToDisplay: WorkspaceMember[] = unaffiliated;
        if (search) {
            membersToDisplay = membersToDisplay.filter(
                (m) =>
                    m.user.name.toLowerCase().includes(search.toLowerCase()) ||
                    m.user.email?.toLowerCase().includes(search.toLowerCase()),
            );
        }

        const sortedMembers = sortBy(
            membersToDisplay,
            (m) => userWorkspaceRolesInOrder.indexOf(m.role),
            (m) => m.user.id,
        );

        const memberRows: WorkspaceMembersRow[] = sortedMembers.map((m) => ({
            id: `member-${m.user.id}`,
            kind: "member" as const,
            member: m,
        }));

        const invitesToShow = search
            ? (pendingInvites ?? []).filter((i) =>
                  i.email?.toLowerCase().includes(search.toLowerCase()),
              )
            : (pendingInvites ?? []);

        const inviteRows: WorkspaceMembersRow[] = invitesToShow.map((i) => ({
            id: `invite-${i.email}`,
            kind: "invite" as const,
            invite: i,
        }));

        return [...orgRows, ...memberRows, ...inviteRows];
    }, [members, pendingInvites, search]);

    if (!membersFetched || !pendingInvitesFetched) {
        return <Loader />;
    }

    return (
        <ViewTable
            data={rows}
            outerBorder
            view={DEFAULT_VIEW}
            config={workspaceMembersViewConfig}
            emptyView={() => <EmptyState header="No members found" />}
        />
    );
};
